import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
    Box, Grid, Stack, Typography, Avatar, Button, IconButton,
    Dialog, DialogTitle, DialogContent, DialogActions, TextField,
    Skeleton, Snackbar, Alert, MenuItem, useTheme,
} from '@mui/material';
import {
    ArrowBackRounded,
    ChevronLeftRounded,
    ChevronRightRounded,
    SendRounded,
    EditRounded,
    PhoneRounded,
    MailRounded,
} from '@mui/icons-material';
import { useParams, useSearchParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import dayjs from 'dayjs';
import 'dayjs/locale/sk';
import 'dayjs/locale/cs';
import 'dayjs/locale/en';

import employeeService from '../../services/employee.service';
import workplaceService from '../../services/workplace.service';
import { usePreferences } from '../../contexts/PreferencesContext';

import {
    DASH, EVENT_TONES, PRESENCE_TONES, inkSurface, resolvePhotoUrl, getInitials,
} from '../dashboard/dashboardTokens';
import { Panel, PanelHead, PanelBody, MetricCell, EmptyState, RowSkeleton } from '../dashboard/Surface';
import SegmentedToggle from '../dashboard/SegmentedToggle';
import AttendanceCalendar from './AttendanceCalendar';
import RhythmSummary from './RhythmSummary';
import DayDetail from './DayDetail';

const DAYJS_LOCALE = { SK: 'sk', EN: 'en', CZ: 'cs' };

// Prepnutie z roka späť na mesiac má trafiť mesiac, ktorý človek naozaj
// čítal — posledný odžitý mesiac toho roka, nie automaticky január.
const monthAnchorWithin = (yearAnchor) => {
    const december = yearAnchor.endOf('year').startOf('month');
    const thisMonth = dayjs().startOf('month');
    return december.isAfter(thisMonth) ? thisMonth : december;
};

const formatMinutes = (minutes) => {
    const total = Math.round(minutes || 0);
    if (total <= 0) return '0 m';
    const h = Math.floor(total / 60);
    const m = total % 60;
    return h > 0 ? `${h} h ${m} m` : `${m} m`;
};

const elapsedSince = (iso) => {
    if (!iso) return null;
    const minutes = dayjs().diff(dayjs(iso), 'minute');
    return minutes >= 0 ? formatMinutes(minutes) : null;
};

/** Tmavá hlavička — kto to je a či je práve v práci, na jeden pohľad. */
const IdentityCard = ({ profile, language, t, onMessage, onEdit }) => {
    const theme = useTheme();
    const ink = inkSurface(theme.palette.mode === 'dark');
    const locale = DAYJS_LOCALE[language] || 'sk';
    const { employee, presence, shiftThought } = profile;
    const photo = resolvePhotoUrl(employee.photo);

    const stateTone = presence.state === 'working'
        ? PRESENCE_TONES.working
        : presence.state === 'on_break' ? PRESENCE_TONES.onBreak : ink.textFaint;

    const stateLabel = (() => {
        if (presence.state === 'working') {
            return t('pages.employeeProfile.presence.working')
                .replace('{since}', dayjs(presence.since).format('H:mm'))
                .replace('{elapsed}', elapsedSince(presence.since) || '');
        }
        if (presence.state === 'on_break') {
            return t('pages.employeeProfile.presence.onBreak')
                .replace('{since}', dayjs(presence.breakSince).format('H:mm'));
        }
        if (presence.lastSeenAt) {
            return t('pages.employeeProfile.presence.lastSeen')
                .replace('{when}', dayjs(presence.lastSeenAt).locale(locale).format('D. M. H:mm'));
        }
        return t('pages.employeeProfile.presence.never');
    })();

    // Odkaz na smenu ukazujeme len kým je čerstvý — týždeň stará nálada nie je stav.
    const freshThought = shiftThought
        && dayjs().diff(dayjs(shiftThought.setAt), 'hour') < 24
        && (shiftThought.text || shiftThought.emoji);

    return (
        <Box
            sx={{
                position: 'relative',
                overflow: 'hidden',
                borderRadius: `${DASH.radius}px`,
                bgcolor: ink.bg,
                border: '1px solid',
                borderColor: ink.border,
                color: ink.text,
                p: { xs: 2.75, sm: 3.5 },
                mb: DASH.gap,
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: -200,
                    right: -140,
                    width: 440,
                    height: 440,
                    borderRadius: '50%',
                    background: 'radial-gradient(circle, rgba(37,99,235,0.30) 0%, rgba(37,99,235,0) 68%)',
                    pointerEvents: 'none',
                },
            }}
        >
            <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={{ xs: 2.5, sm: 3 }}
                sx={{ position: 'relative' }}
                alignItems={{ xs: 'flex-start', sm: 'center' }}
            >
                <Avatar
                    src={photo || undefined}
                    alt={employee.name}
                    sx={{
                        width: 76,
                        height: 76,
                        flexShrink: 0,
                        fontFamily: DASH.display,
                        fontSize: '1.5rem',
                        fontWeight: 600,
                        bgcolor: 'rgba(255,255,255,0.12)',
                        color: '#fff',
                        border: '1px solid',
                        borderColor: ink.hairline,
                    }}
                >
                    {getInitials(employee.name)}
                </Avatar>

                <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography
                        component="h1"
                        sx={{
                            fontFamily: DASH.display,
                            fontSize: { xs: '1.5rem', sm: '1.875rem' },
                            fontWeight: 700,
                            letterSpacing: '-0.03em',
                            lineHeight: 1.1,
                        }}
                    >
                        {employee.name}
                    </Typography>
                    <Typography sx={{ mt: 0.5, fontSize: '0.875rem', color: ink.textMuted }}>
                        {[employee.position, employee.workplaces.map((w) => w.name).join(', ')]
                            .filter(Boolean).join(' · ') || t('pages.employeeProfile.noPosition')}
                    </Typography>

                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.5 }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: stateTone, flexShrink: 0 }} />
                        <Typography sx={{ fontSize: '0.875rem', fontWeight: 550 }}>
                            {stateLabel}
                        </Typography>
                    </Stack>

                    {freshThought && (
                        <Typography sx={{ mt: 1, fontSize: '0.8125rem', color: ink.textFaint }}>
                            {[shiftThought.emoji, shiftThought.text].filter(Boolean).join('  ')}
                        </Typography>
                    )}
                </Box>

                <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
                    <Button
                        onClick={onMessage}
                        startIcon={<SendRounded sx={{ fontSize: 16 }} />}
                        sx={{
                            bgcolor: '#FFFFFF', color: ink.bg, fontWeight: 650, px: 2, height: 38,
                            borderRadius: '19px', '&:hover': { bgcolor: 'rgba(255,255,255,0.88)' },
                        }}
                    >
                        {t('pages.employeeProfile.actions.message')}
                    </Button>
                    <Button
                        onClick={onEdit}
                        startIcon={<EditRounded sx={{ fontSize: 16 }} />}
                        sx={{
                            color: ink.text, fontWeight: 550, px: 2, height: 38, borderRadius: '19px',
                            border: '1px solid', borderColor: ink.hairline,
                            '&:hover': { bgcolor: 'rgba(255,255,255,0.06)' },
                        }}
                    >
                        {t('pages.employeeProfile.actions.edit')}
                    </Button>
                </Stack>
            </Stack>

            {(employee.phone || employee.email) && (
                <Stack
                    direction="row"
                    spacing={3}
                    flexWrap="wrap"
                    useFlexGap
                    sx={{ position: 'relative', mt: 3, pt: 2.25, borderTop: `1px solid ${ink.hairline}` }}
                >
                    {employee.phone && (
                        <Stack
                            component="a"
                            href={`tel:${employee.phone}`}
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            sx={{ color: ink.textMuted, textDecoration: 'none', '&:hover': { color: ink.text } }}
                        >
                            <PhoneRounded sx={{ fontSize: 16 }} />
                            <Typography sx={{ fontSize: '0.875rem', ...DASH.numeric }}>{employee.phone}</Typography>
                        </Stack>
                    )}
                    {employee.email && (
                        <Stack
                            component="a"
                            href={`mailto:${employee.email}`}
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            sx={{ color: ink.textMuted, textDecoration: 'none', '&:hover': { color: ink.text } }}
                        >
                            <MailRounded sx={{ fontSize: 16 }} />
                            <Typography sx={{ fontSize: '0.875rem' }}>{employee.email}</Typography>
                        </Stack>
                    )}
                </Stack>
            )}
        </Box>
    );
};

/** Fakty, ktoré sa nemenia každý deň — nech nezavadzajú hore. */
const FactRow = ({ label, value }) => (
    <Stack direction="row" justifyContent="space-between" spacing={2} sx={{ py: 1.125, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography variant="body2" color="text.secondary">{label}</Typography>
        <Typography variant="body2" sx={{ fontWeight: 550, textAlign: 'right' }}>{value}</Typography>
    </Stack>
);

const EmployeeProfilePage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { t, language } = usePreferences();
    const [searchParams, setSearchParams] = useSearchParams();
    const locale = DAYJS_LOCALE[language] || 'sk';

    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);
    const [toast, setToast] = useState(null);

    // Obdobie je kalendárne, nie kĺzavé okno. „21 z 22 pracovných dní" dáva
    // zmysel len vtedy, keď je jasné, ktorý mesiac to bol — výplata ani
    // dovolenka sa nerátajú na posledných 30 dní.
    const { scope, rangeKey } = useMemo(() => {
        const yearParam = searchParams.get('year');
        if (yearParam && /^\d{4}$/.test(yearParam)) {
            return { scope: 'year', rangeKey: yearParam };
        }
        const monthParam = searchParams.get('month');
        if (monthParam && /^\d{4}-\d{2}$/.test(monthParam)
            && dayjs(`${monthParam}-01`, 'YYYY-MM-DD').isValid()) {
            return { scope: 'month', rangeKey: monthParam };
        }
        return { scope: 'month', rangeKey: dayjs().format('YYYY-MM') };
    }, [searchParams]);

    // Obdobie drží reťazec, nie dayjs. Objekt by bol pri každej zmene URL nový,
    // takže by aj klik na deň vyzeral ako zmena obdobia a znovu ťahal profil.
    const anchor = useMemo(
        () => dayjs(scope === 'year' ? `${rangeKey}-01-01` : `${rangeKey}-01`, 'YYYY-MM-DD'),
        [scope, rangeKey]
    );

    const selectedDate = searchParams.get('date');

    const [messageOpen, setMessageOpen] = useState(false);
    const [messageBody, setMessageBody] = useState('');
    const [messageTitle, setMessageTitle] = useState('Admin');
    const [sending, setSending] = useState(false);

    const [editOpen, setEditOpen] = useState(false);
    const [editForm, setEditForm] = useState({ name: '', position: '', email: '', phone_number: '' });
    const [editWorkplaces, setEditWorkplaces] = useState([]);
    const [allWorkplaces, setAllWorkplaces] = useState([]);
    const [saving, setSaving] = useState(false);

    const fetchProfile = useCallback(() => {
        setLoading(true);
        employeeService.getProfile(id, scope === 'year'
            ? { year: rangeKey }
            : { month: rangeKey })
            .then((res) => { setProfile(res.data); setNotFound(false); })
            .catch((err) => {
                if (err?.response?.status === 404) setNotFound(true);
                else console.error('Error fetching employee profile', err);
            })
            .finally(() => setLoading(false));
    }, [id, scope, rangeKey]);

    useEffect(() => { fetchProfile(); }, [fetchProfile]);

    const setParam = useCallback((key, value) => {
        const next = new URLSearchParams(searchParams);
        if (value == null) next.delete(key);
        else next.set(key, String(value));
        setSearchParams(next, { replace: true });
    }, [searchParams, setSearchParams]);

    const dayPanelRef = useRef(null);

    // Detail dňa je pod kalendárom, často mimo obrazovky. Bez posunu vyzerá
    // klik na deň, akoby sa nestalo nič. Posúvame len keď naozaj nie je vidieť.
    const selectDay = useCallback((date) => {
        setParam('date', date);
        requestAnimationFrame(() => {
            const node = dayPanelRef.current;
            if (!node) return;
            const { top, bottom } = node.getBoundingClientRect();
            const visible = top >= 0 && bottom <= window.innerHeight;
            if (!visible) node.scrollIntoView({ behavior: 'smooth', block: 'center' });
        });
    }, [setParam]);

    // Bez dátumu v URL padne výber na posledný deň so záznamom — stránka nikdy
    // neotvorí prázdny deň, ak nejaký s obsahom existuje.
    const activeDate = useMemo(() => {
        if (selectedDate && /^\d{4}-\d{2}-\d{2}$/.test(selectedDate)) return selectedDate;
        const days = profile?.days || [];
        return days.length ? days[days.length - 1].date : dayjs().format('YYYY-MM-DD');
    }, [selectedDate, profile]);

    const activeDay = useMemo(
        () => (profile?.days || []).find((d) => d.date === activeDate) || null,
        [profile, activeDate]
    );

    const activeEvents = useMemo(
        () => (profile?.events || []).filter((e) => e.date === activeDate),
        [profile, activeDate]
    );

    const unit = scope === 'year' ? 'year' : 'month';

    // Zmena obdobia zahodí vybraný deň — patril do rozsahu, ktorý už nie je
    // na obrazovke.
    const setRange = useCallback((nextScope, nextAnchor) => {
        const next = new URLSearchParams(searchParams);
        next.delete('date');
        if (nextScope === 'year') {
            next.delete('month');
            next.set('year', nextAnchor.format('YYYY'));
        } else {
            next.delete('year');
            next.set('month', nextAnchor.format('YYYY-MM'));
        }
        setSearchParams(next, { replace: true });
    }, [searchParams, setSearchParams]);

    const rangeLabel = scope === 'year'
        ? anchor.format('YYYY')
        : anchor.locale(locale).format('MMMM YYYY');

    const atPresent = anchor.isSame(dayjs(), unit);

    // Prebiehajúci mesiac má dáta len po dnešok — hlavička nemá sľubovať viac.
    const visibleRangeTo = useMemo(() => {
        const today = dayjs();
        const rangeEnd = dayjs(profile?.range?.to || today, 'YYYY-MM-DD');
        return rangeEnd.isAfter(today, 'day') ? today : rangeEnd;
    }, [profile]);

    const scopeOptions = useMemo(() => [
        { value: 'month', label: t('pages.employeeProfile.rhythm.scopeMonth') },
        { value: 'year', label: t('pages.employeeProfile.rhythm.scopeYear') },
    ], [t]);

    const openEdit = () => {
        if (!profile) return;
        setEditForm({
            name: profile.employee.name || '',
            position: profile.employee.position || '',
            email: profile.employee.email || '',
            phone_number: profile.employee.phone || '',
        });
        setEditWorkplaces(profile.employee.workplaces.map((w) => w.id));
        workplaceService.getWorkplaces()
            .then((res) => setAllWorkplaces(Array.isArray(res.data) ? res.data : []))
            .catch((err) => console.error('Error fetching workplaces', err));
        setEditOpen(true);
    };

    const handleSend = async () => {
        if (!messageBody.trim()) return;
        setSending(true);
        try {
            await employeeService.sendNotification(id, messageTitle.trim() || 'Admin', messageBody.trim());
            setToast({ severity: 'success', text: t('pages.employeeProfile.message.sent') });
            setMessageOpen(false);
            setMessageBody('');
        } catch (err) {
            console.error('Error sending notification', err);
            setToast({ severity: 'error', text: t('pages.employeeProfile.message.failed') });
        } finally {
            setSending(false);
        }
    };

    const handleSave = async () => {
        if (!editForm.name.trim()) return;
        setSaving(true);
        try {
            await employeeService.updateEmployee(id, {
                name: editForm.name.trim(),
                position: editForm.position.trim(),
                email: editForm.email.trim(),
                phone_number: editForm.phone_number.trim(),
            });
            await employeeService.assignWorkplaces(id, editWorkplaces);
            setToast({ severity: 'success', text: t('pages.employeeProfile.edit.saved') });
            setEditOpen(false);
            fetchProfile();
        } catch (err) {
            console.error('Error updating employee', err);
            setToast({ severity: 'error', text: t('pages.employeeProfile.edit.failed') });
        } finally {
            setSaving(false);
        }
    };

    if (notFound) {
        return (
            <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: 'background.default', minHeight: '100vh' }}>
                <Panel>
                    <EmptyState
                        title={t('pages.employeeProfile.notFound')}
                        hint={t('pages.employeeProfile.notFoundHint')}
                        action={(
                            <Button component={RouterLink} to="/employees" variant="contained" sx={{ borderRadius: '999px' }}>
                                {t('pages.employeeProfile.backToList')}
                            </Button>
                        )}
                    />
                </Panel>
            </Box>
        );
    }

    return (
        <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: 'background.default', minHeight: '100vh' }}>
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 2 }}>
                <IconButton size="small" onClick={() => navigate('/employees')} aria-label={t('pages.employeeProfile.backToList')}>
                    <ArrowBackRounded fontSize="small" />
                </IconButton>
                <Typography
                    component={RouterLink}
                    to="/employees"
                    variant="body2"
                    sx={{ color: 'text.secondary', textDecoration: 'none', '&:hover': { color: 'text.primary' } }}
                >
                    {t('pages.employeeProfile.backToList')}
                </Typography>
            </Stack>

            {loading && !profile ? (
                <Skeleton variant="rounded" height={210} sx={{ borderRadius: `${DASH.radius}px`, mb: DASH.gap }} />
            ) : profile && (
                <IdentityCard
                    profile={profile}
                    language={language}
                    t={t}
                    onMessage={() => setMessageOpen(true)}
                    onEdit={openEdit}
                />
            )}

            {/* Ako chodí — pás dní a priemery za zvolené obdobie */}
            <Panel sx={{ mb: DASH.gap }}>
                <PanelHead
                    title={t('pages.employeeProfile.rhythm.title')}
                    meta={profile ? t('pages.dashboard.analytics.periodRange')
                        .replace('{from}', dayjs(profile.range.from, 'YYYY-MM-DD').locale(locale).format('D. M. YYYY'))
                        .replace('{to}', visibleRangeTo.locale(locale).format('D. M. YYYY')) : undefined}
                    actions={(
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                            <Stack
                                direction="row"
                                alignItems="center"
                                sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '999px', px: 0.25 }}
                            >
                                <IconButton
                                    size="small"
                                    onClick={() => setRange(scope, anchor.subtract(1, unit))}
                                    aria-label={t('pages.employeeProfile.rhythm.previous')}
                                >
                                    <ChevronLeftRounded fontSize="small" />
                                </IconButton>
                                <Typography
                                    sx={{
                                        minWidth: scope === 'year' ? 52 : 128,
                                        textAlign: 'center',
                                        fontSize: '0.875rem',
                                        fontWeight: 600,
                                        textTransform: 'capitalize',
                                        ...DASH.numeric,
                                    }}
                                >
                                    {rangeLabel}
                                </Typography>
                                <IconButton
                                    size="small"
                                    disabled={atPresent}
                                    onClick={() => setRange(scope, anchor.add(1, unit))}
                                    aria-label={t('pages.employeeProfile.rhythm.next')}
                                >
                                    <ChevronRightRounded fontSize="small" />
                                </IconButton>
                            </Stack>
                            <SegmentedToggle
                                value={scope}
                                onChange={(value) => setRange(
                                    value,
                                    value === 'year' ? anchor.startOf('year') : monthAnchorWithin(anchor)
                                )}
                                options={scopeOptions}
                                size="sm"
                            />
                        </Stack>
                    )}
                />
                <PanelBody>
                    {loading || !profile ? (
                        <Skeleton variant="rounded" height={140} />
                    ) : (
                        <>
                            <RhythmSummary profile={profile} t={t} />
                            <AttendanceCalendar
                                days={profile.days}
                                from={profile.range.from}
                                to={profile.range.to}
                                selectedDate={activeDate}
                                onSelect={selectDay}
                                language={language}
                                t={t}
                            />
                            <Stack
                                direction={{ xs: 'column', md: 'row' }}
                                sx={{ flexWrap: 'wrap', mt: 3, pt: 3, borderTop: '1px solid', borderColor: 'divider' }}
                                useFlexGap
                            >
                                {[
                                    { key: 'arrival', label: t('pages.dashboard.analytics.avgArrival'), value: profile.totals.avgArrival },
                                    { key: 'departure', label: t('pages.dashboard.analytics.avgDeparture'), value: profile.totals.avgDeparture },
                                    { key: 'work', label: t('pages.dashboard.analytics.avgWork'), value: profile.totals.avgNetWorkStr },
                                    { key: 'break', label: t('pages.dashboard.analytics.avgBreak'), value: profile.totals.avgBreakStr },
                                    { key: 'total', label: t('pages.employeeProfile.rhythm.total'), value: profile.totals.totalNetWorkStr },
                                    { key: 'days', label: t('pages.employeeProfile.rhythm.daysWorked'), value: profile.totals.daysWorked },
                                ].map((tile, idx) => (
                                    <MetricCell key={tile.key} label={tile.label} value={tile.value} isFirst={idx === 0} />
                                ))}
                            </Stack>
                        </>
                    )}
                </PanelBody>
            </Panel>

            <Grid container spacing={DASH.gap} sx={{ mb: DASH.gap, alignItems: 'stretch' }}>
                {/* Vybraný deň */}
                <Grid size={{ xs: 12, lg: 7 }} sx={{ display: 'flex' }} ref={dayPanelRef}>
                    <Panel>
                        <PanelHead
                            title={dayjs(activeDate, 'YYYY-MM-DD').locale(locale).format('dddd D. MMMM')}
                        />
                        <PanelBody>
                            {loading || !profile ? <RowSkeleton rows={3} /> : (
                                <DayDetail
                                    day={activeDay}
                                    events={activeEvents}
                                    totals={profile.totals}
                                    t={t}
                                />
                            )}
                        </PanelBody>
                    </Panel>
                </Grid>

                {/* Čo si všimnúť */}
                <Grid size={{ xs: 12, lg: 5 }} sx={{ display: 'flex' }}>
                    <Panel>
                        <PanelHead title={t('pages.employeeProfile.attention.title')} />
                        <PanelBody>
                            {loading || !profile ? <RowSkeleton rows={3} /> : (() => {
                                const items = [];
                                if (profile.flags.autoEndedCount > 0) {
                                    items.push({
                                        key: 'auto',
                                        tone: EVENT_TONES.break_start,
                                        text: t('pages.employeeProfile.attention.autoEnded')
                                            .replace('{count}', String(profile.flags.autoEndedCount)),
                                    });
                                }
                                if (profile.flags.staleOpenCount > 0) {
                                    items.push({
                                        key: 'open',
                                        tone: EVENT_TONES.break_start,
                                        text: t('pages.employeeProfile.attention.staleOpen')
                                            .replace('{count}', String(profile.flags.staleOpenCount)),
                                    });
                                }
                                if (profile.requests.pending > 0) {
                                    items.push({
                                        key: 'requests',
                                        tone: '#2563EB',
                                        text: t('pages.employeeProfile.attention.pendingRequests')
                                            .replace('{count}', String(profile.requests.pending)),
                                        to: '/requests',
                                    });
                                }
                                if (!items.length) {
                                    return (
                                        <EmptyState
                                            title={t('pages.employeeProfile.attention.clearTitle')}
                                            hint={t('pages.employeeProfile.attention.clearHint')}
                                        />
                                    );
                                }

                                return (
                                    <Stack spacing={0}>
                                        {items.map((item) => (
                                            <Stack
                                                key={item.key}
                                                direction="row"
                                                spacing={1.5}
                                                alignItems="center"
                                                sx={{ py: 1.375, borderBottom: '1px solid', borderColor: 'divider' }}
                                            >
                                                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: item.tone, flexShrink: 0 }} />
                                                <Typography variant="body2" sx={{ flex: 1 }}>{item.text}</Typography>
                                                {item.to && (
                                                    <Button
                                                        component={RouterLink}
                                                        to={item.to}
                                                        size="small"
                                                        sx={{ borderRadius: '999px', flexShrink: 0 }}
                                                    >
                                                        {t('pages.employeeProfile.attention.open')}
                                                    </Button>
                                                )}
                                            </Stack>
                                        ))}
                                    </Stack>
                                );
                            })()}
                        </PanelBody>
                    </Panel>
                </Grid>
            </Grid>

            {/* Fakty */}
            <Panel>
                <PanelHead title={t('pages.employeeProfile.facts.title')} />
                <PanelBody>
                    {loading || !profile ? <RowSkeleton rows={4} /> : (
                        <Grid container spacing={{ xs: 0, md: 4 }}>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <FactRow
                                    label={t('pages.employeeProfile.facts.startDate')}
                                    value={profile.employee.employmentStartDate
                                        ? dayjs(profile.employee.employmentStartDate, 'YYYY-MM-DD').locale(locale).format('D. M. YYYY')
                                        : '–'}
                                />
                                <FactRow
                                    label={t('pages.employeeProfile.facts.birthday')}
                                    value={profile.employee.birthday
                                        ? dayjs(profile.employee.birthday, 'YYYY-MM-DD').locale(locale).format('D. MMMM')
                                        : '–'}
                                />
                                <FactRow
                                    label={t('pages.employeeProfile.facts.workplaces')}
                                    value={profile.employee.workplaces.length
                                        ? profile.employee.workplaces.map((w) => w.name).join(', ')
                                        : t('pages.employeeProfile.facts.noWorkplace')}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <FactRow
                                    label={t('pages.employeeProfile.facts.device')}
                                    value={profile.device
                                        ? [profile.device.deviceModel || profile.device.deviceName, profile.device.systemName].filter(Boolean).join(' · ')
                                        : t('pages.employeeProfile.facts.noDevice')}
                                />
                                <FactRow
                                    label={t('pages.employeeProfile.facts.appVersion')}
                                    value={profile.device?.appVersion || '–'}
                                />
                                <FactRow
                                    label={t('pages.employeeProfile.facts.lastSeen')}
                                    value={profile.presence.lastSeenAt
                                        ? dayjs(profile.presence.lastSeenAt).locale(locale).format('D. M. YYYY H:mm')
                                        : '–'}
                                />
                            </Grid>
                        </Grid>
                    )}
                </PanelBody>
            </Panel>

            {/* Správa do aplikácie */}
            <Dialog open={messageOpen} onClose={() => setMessageOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle sx={{ fontFamily: DASH.display, fontWeight: 600 }}>
                    {t('pages.employeeProfile.message.title').replace('{name}', profile?.employee.name || '')}
                </DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        margin="dense"
                        label={t('pages.employeeProfile.message.subject')}
                        value={messageTitle}
                        onChange={(e) => setMessageTitle(e.target.value)}
                    />
                    <TextField
                        fullWidth
                        multiline
                        minRows={3}
                        margin="dense"
                        label={t('pages.employeeProfile.message.body')}
                        value={messageBody}
                        onChange={(e) => setMessageBody(e.target.value)}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setMessageOpen(false)}>{t('common.cancel')}</Button>
                    <Button
                        variant="contained"
                        onClick={handleSend}
                        disabled={sending || !messageBody.trim()}
                        sx={{ borderRadius: '999px' }}
                    >
                        {t('pages.employeeProfile.message.send')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Úprava údajov */}
            <Dialog open={editOpen} onClose={() => setEditOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle sx={{ fontFamily: DASH.display, fontWeight: 600 }}>
                    {t('pages.employeeProfile.edit.title')}
                </DialogTitle>
                <DialogContent>
                    {['name', 'position', 'email', 'phone_number'].map((field) => (
                        <TextField
                            key={field}
                            fullWidth
                            margin="dense"
                            label={t(`pages.employeeProfile.edit.${field}`)}
                            value={editForm[field]}
                            onChange={(e) => setEditForm((prev) => ({ ...prev, [field]: e.target.value }))}
                        />
                    ))}
                    <TextField
                        select
                        fullWidth
                        margin="dense"
                        label={t('pages.employeeProfile.edit.workplaces')}
                        value={editWorkplaces}
                        onChange={(e) => setEditWorkplaces(
                            typeof e.target.value === 'string' ? e.target.value.split(',').map(Number) : e.target.value
                        )}
                        slotProps={{
                            select: {
                                multiple: true,
                                renderValue: (selected) => allWorkplaces
                                    .filter((w) => selected.includes(w.id))
                                    .map((w) => w.name)
                                    .join(', '),
                            },
                        }}
                    >
                        {allWorkplaces.map((workplace) => (
                            <MenuItem key={workplace.id} value={workplace.id}>{workplace.name}</MenuItem>
                        ))}
                    </TextField>
                    {!editWorkplaces.length && (
                        <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 1 }}>
                            {t('pages.employeeProfile.edit.noWorkplaceWarning')}
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setEditOpen(false)}>{t('common.cancel')}</Button>
                    <Button
                        variant="contained"
                        onClick={handleSave}
                        disabled={saving || !editForm.name.trim()}
                        sx={{ borderRadius: '999px' }}
                    >
                        {t('common.save')}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={Boolean(toast)}
                autoHideDuration={4000}
                onClose={() => setToast(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                {toast ? <Alert severity={toast.severity} onClose={() => setToast(null)}>{toast.text}</Alert> : null}
            </Snackbar>
        </Box>
    );
};

export default EmployeeProfilePage;
